/**
 * Review Service
 * Updated for new backend API structure
 */

import api from './api';

export const reviewService = {
  /**
   * Get reviews for a product
   * @param {number} productId - Product ID
   * @param {Object} params - Query parameters
   * @param {number} params.page - Page number (1-based)
   * @param {number} params.limit - Items per page
   * @param {string} params.sortBy - Sort option (newest, oldest, highest, lowest, helpful)
   * @returns {Promise<Object>} Reviews with pagination
   */
  getProductReviews: async (productId, params = {}) => {
    try {
      const response = await api.get(`/products/${productId}/reviews`, {
        params: {
          page: params.page || 1,
          limit: params.limit || 10,
          sortBy: params.sortBy || 'newest'
        }
      });
      // Response: { success, data: [...], pagination: {...} }
      return response.data;
    } catch (error) {
      console.error('[ReviewService] Get product reviews error:', error);
      throw error;
    }
  },

  /**
   * Get review summary for a product
   * @param {number} productId - Product ID
   * @returns {Promise<Object>} Review summary with rating distribution
   */
  getReviewSummary: async (productId) => {
    try {
      const response = await api.get(`/products/${productId}/reviews/summary`);
      return response.data;
    } catch (error) {
      console.error('[ReviewService] Get review summary error:', error);
      throw error;
    }
  },

  /**
   * Check if user can review a product
   * @param {number} productId - Product ID
   * @returns {Promise<Object>} { canReview: boolean, reason: string|null }
   */
  canReview: async (productId) => {
    try {
      const response = await api.get(`/products/${productId}/reviews/can-review`);
      return response.data;
    } catch (error) {
      console.error('[ReviewService] Can review check error:', error);
      throw error;
    }
  },

  /**
   * Create a review for a product
   * @param {number} productId - Product ID
   * @param {Object} reviewData - Review data
   * @param {number} reviewData.rating - Rating (1-5)
   * @param {string} reviewData.title - Review title
   * @param {string} reviewData.comment - Review comment
   * @param {number} reviewData.orderId - Order ID (optional, for verified purchase)
   * @param {Array} reviewData.images - Uploaded image URLs (optional)
   * @returns {Promise<Object>} Created review
   */
  createReview: async (productId, reviewData) => {
    try {
      // Backend requires productId in the request body as well as the URL
      const payload = {
        ...reviewData,
        productId: productId
      };
      const response = await api.post(`/products/${productId}/reviews`, payload);
      return response.data;
    } catch (error) {
      console.error('[ReviewService] Create review error:', error);
      throw error;
    }
  },

  /**
   * Update a review
   * @param {number} reviewId - Review ID
   * @param {Object} reviewData - Updated review data
   * @returns {Promise<Object>} Updated review
   */
  updateReview: async (reviewId, reviewData) => {
    try {
      const response = await api.put(`/reviews/${reviewId}`, reviewData);
      return response.data;
    } catch (error) {
      console.error('[ReviewService] Update review error:', error);
      throw error;
    }
  },

  /**
   * Delete a review
   * @param {number} reviewId - Review ID
   * @returns {Promise<Object>} Deletion confirmation
   */
  deleteReview: async (reviewId) => {
    try {
      const response = await api.delete(`/reviews/${reviewId}`);
      return response.data;
    } catch (error) {
      console.error('[ReviewService] Delete review error:', error);
      throw error;
    }
  },

  /**
   * Toggle helpful vote on a review
   * @param {number} reviewId - Review ID
   * @returns {Promise<Object>} { reviewId, helpfulCount, hasVoted }
   */
  toggleHelpful: async (reviewId) => {
    try {
      const response = await api.post(`/reviews/${reviewId}/helpful`);
      return response.data;
    } catch (error) {
      console.error('[ReviewService] Toggle helpful error:', error);
      throw error;
    }
  },

  /**
   * Upload a review image
   * @param {File} file - Image file
   * @returns {Promise<Object>} { url, thumbnailUrl, fileSize, width, height }
   */
  uploadImage: async (file) => {
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await api.post('/uploads/review-images', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      return response.data.data;
    } catch (error) {
      console.error('[ReviewService] Upload image error:', error);
      throw error;
    }
  },

  /**
   * Upload multiple review images
   * @param {File[]} files - Image files (max 3)
   * @returns {Promise<Array>} Array of uploaded image data
   */
  uploadImages: async (files) => {
    try {
      const formData = new FormData();
      files.forEach(file => formData.append('files', file));

      const response = await api.post('/uploads/review-images/batch', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      return response.data.data;
    } catch (error) {
      console.error('[ReviewService] Upload images error:', error);
      throw error;
    }
  }
};

export default reviewService;
